import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor;
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { shallowEqual } from "abreact/incubator/ui/app/router/RouterUtils";
import { LazyCmpLoader } from "abreact/incubator/ui/lazy/LazyCmpLoader";
import { Assert } from "abweb/utils/Assert";
import { action, makeObservable, observable } from "mobx";
import React from "react";

// Below 'renderView' is ultimately invoked from an application layout component that is an observable component;
// as such, whenever below 'observable routeProps' changes it will cause a re-render of the 'mainArea'
export let RouteServicesRenderer = (_class = class RouteServicesRenderer {
  constructor(routeServices, routeHocServices, viewCmpImportSpec, hocFnImportSpecs) {
    // When this changes, it causes observers to re-render (re-invoke renderView), see ABSDK-4998
    _initializerDefineProperty(this, "_forceRerenderView", _descriptor, this);
    // Unique symbol that is recalculated on every forceUpdate invocation
    // so that MemoizedLazyCmpLoader will rerender even when props are 'shallow equals'
    _defineProperty(this, "forceUpdateSymbol", Symbol());
    // @ts-ignore strict mode catches that not assigned in constructor (and type does not include undefined)
    _defineProperty(this, "_rawRouteProps", void 0);
    this.routeServices = routeServices;
    this.routeHocServices = routeHocServices;
    this.viewCmpImportSpec = viewCmpImportSpec;
    this.hocFnImportSpecs = hocFnImportSpecs;
    makeObservable(this);
  }
  triggerRerenderView() {
    this._forceRerenderView++; // triggers AbRouteRender 'currentRH.renderView()' invocation
  }

  /**
   * @returns shallow COPY of current route props.
   * The returned props do NOT the injected RouteServicesProps props (e.g., routeServices)
   */
  getRawRouteProps() {
    return {
      ...this._rawRouteProps
    };
  }
  setRawRouteProps(props, forceUpdate = false) {
    // Make shallow copy so that external caller can't make shallow prop change after calling setRawRouteProps (aka updateRoute)
    this._rawRouteProps = {
      ...props
    };
    if (forceUpdate) {
      this.forceUpdateSymbol = Symbol();
    }
    // AbRouteRender will re-invoke renderView,
    // and relies on Memoization to suppress unnecessary re-renders
    this.triggerRerenderView();
  }

  // Inject routeServices and routeHocServices into rawRouteProps to get full RoutePropsT
  get propsPlusRouteServices() {
    // Last one in wins, so this.route*Services passed in last
    return {
      ...this._rawRouteProps,
      routeServices: this.routeServices,
      routeHocServices: this.routeHocServices
    };
  }

  // Called on route changes, and on updateRoute calls with forceRender=true arg value.
  // Relies on callers being mobx observers so that they'll automatically re-invoke whenever forceUpdateVal incremented.
  renderView() {
    // Access observable _forceRerenderView so that everytime it is incremented,
    // the 'observer' calls will re-invoke renderView
    Assert.assert(this._forceRerenderView > -1);
    if (this._rawRouteProps == null) {
      throw new Error("setRouteProps must be invoked before renderView is invoked.");
    }
    // Use memoized component so that it won't rerender unless props change (via shallow equals)
    // OR on forceUpdate workflow (which generates new forceUpdateSymbol)
    return /*#__PURE__*/React.createElement(MemoizedLazyCmpLoader, {
      cmpImportSpec: this.viewCmpImportSpec,
      hocFnImportSpecs: this.hocFnImportSpecs,
      cmpProps: this.propsPlusRouteServices,
      forceUpdateTrigger: this.forceUpdateSymbol
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_forceRerenderView", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "triggerRerenderView", [action], Object.getOwnPropertyDescriptor(_class.prototype, "triggerRerenderView"), _class.prototype), _class);

// Adds additional 'forceUpdateTrigger: Symbol' prop to support forcing Memoized component to rerender
// TODO BLM: make better generic version of this that also supports additional forceUpdateTrigger prop
const MemoizedLazyCmpLoader = /*#__PURE__*/React.memo(props => /*#__PURE__*/React.createElement(LazyCmpLoader, props), (oldProps, newProps) =>
// Note cmpImportSpec and hocFnImportSpecs are 'readonly', so never change and can be ignored in propsAreEqual
oldProps.forceUpdateTrigger === newProps.forceUpdateTrigger && shallowEqual(oldProps.cmpProps, newProps.cmpProps));
MemoizedLazyCmpLoader.displayName = "MemoizedLazyCmpLoader";
//# sourceMappingURL=RouteServicesRenderer.js.map
